import { ActionModal } from '@frontend/components/modal';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import { useBlobRepository } from '@frontend/repository';
import { StockExtended } from '@frontend/stock/types';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTransactionListener } from '../../../hooks/useTransactionListener/useTransactionListener';
import { useAppSelector } from '../../../redux/store';
import { useLicenseRepository } from '../../../utils/license-repository';

export interface StockCardProps {
    stock: StockExtended;
    onRemove?: () => void;
}

const REMOVE_STOCK_MODAL_ID = 'remove-stock-modal';

export const useStockCard = (props: StockCardProps) => {
    const intl = useIntl();
    const { stock, onRemove } = props;
    const blobRepository = useBlobRepository();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const pendingTransactionIdRef = useRef<string | null>(null);
    const { openModal, closeModal, updateModal } = useModals();
    const navigationState = useAppSelector((store) => store.navigation);
    const { getFunctionalitySetting, getFunctionality } = useLicenseRepository();
    const spotsLayoutSettings = getFunctionalitySetting(getFunctionality(Application.SPOTS, Feature.LAYOUT)?.id ?? '');

    const productName = stock.product?.data?.name ?? stock.product?.name ?? '/';
    const attributes = (stock.product as { data?: { attributes?: Record<string, string> } })?.data?.attributes;

    const isEquipment = stock.equipment_id != null;
    const sn = isEquipment ? stock.equipment?.name : null;
    const tag = isEquipment ? stock.equipment_tag?.name : null;
    const productTitle = sn != null || tag != null ? `${productName}${sn != null ? ` (SN: ${sn})` : ''}${tag != null ? ` (Tag: ${tag})` : ''}` : productName;

    useTransactionListener({
        id: 'stock-card-' + stock.id,
        onComplete: (transaction) => {
            if (pendingTransactionIdRef.current && transaction.id === pendingTransactionIdRef.current) {
                pendingTransactionIdRef.current = null;
                setIsRemoving(false);
                closeModal(REMOVE_STOCK_MODAL_ID);
                onRemove?.();
            }
        }
    });

    useEffect(() => {
        if (!stock.product_id || !blobRepository) return;
        const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
        if (!accountId) return;
        new Product({ id: stock.product_id, account_id: accountId } as IProduct)
            .getBlobImage(blobRepository)
            .then((url) => setImageUrl(url ?? null))
            .catch(() => setImageUrl(null));
    }, [stock.product_id, blobRepository, navigationState.user?.accountId, navigationState.iot?.account_id]);

    const renderRemoveModal = (loading: boolean, onConfirm: () => void) => (
        <ActionModal
            title={productTitle}
            cancel={{
                onCancel: () => closeModal(REMOVE_STOCK_MODAL_ID),
                label: CommonMessage.BUTTONS.CANCEL,
                hide: loading
            }}
            confirm={{
                onConfirm,
                label: CommonMessage.BUTTONS.CONFIRM,
                isLoading: loading,
                disabled: loading
            }}>
            {intl.formatMessage({
                id: 'stock-card.remove-confirmation',
                defaultMessage: 'Are you sure you want to remove this stock?'
            })}
        </ActionModal>
    );

    const trxWokflow = spotsLayoutSettings?.transaction_workflow_id;
    const pkgWorkflow = spotsLayoutSettings?.pick_up_package_workflow_id;
    const canRemove = !!(navigationState.user?.id && trxWokflow && pkgWorkflow);

    const handleRemoveConfirm = async () => {
        if (isRemoving) return;
        setIsRemoving(true);
        updateModal(REMOVE_STOCK_MODAL_ID, () => renderRemoveModal(true, handleRemoveConfirm));
        try {
            const transaction = await TransactionClient.postTransaction(stock.account_id, {
                type: TransactionType.AUDIT,
                user_id: navigationState.user?.id ?? null,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                application: Application.SPOTS,
                feature: Feature.AUDIT_TRANSACTION,
                workflow_id: (spotsLayoutSettings?.transaction_workflow_id ?? undefined) as string | undefined,
                state: TransactionStateName.TRANSACTION_CREATED
            });

            await PackageClient.postPackage(transaction.account_id, transaction.id, {
                type: PackageType.PICK_UP,
                product_id: stock.product_id,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                quantity: 1,
                workflow_id: (spotsLayoutSettings?.pick_up_package_workflow_id ?? undefined) as string | undefined,
                user_id: navigationState.user?.id ?? null,
                state: PackageStateName.PACKAGE_CREATED,
                application: Application.SPOTS,
                feature: Feature.AUDIT_TRANSACTION
            });

            if (navigationState.user?.id) {
                TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.PICKED_UP_BY,
                    user_id: navigationState.user.id
                });
            }

            pendingTransactionIdRef.current = transaction.id;

            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
        } catch (error) {
            console.error('Failed to remove stock', error);
            setIsRemoving(false);
            updateModal(REMOVE_STOCK_MODAL_ID, () => renderRemoveModal(false, handleRemoveConfirm));
        }
    };

    const handleRemoveClick = () => {
        if (!canRemove) return;
        openModal(REMOVE_STOCK_MODAL_ID, () => renderRemoveModal(false, handleRemoveConfirm), {}, { className: 'max-w-[900px]' });
    };

    return {
        imageUrl,
        productTitle,
        attributes,
        handleRemoveClick,
        isRemoving,
        canRemove
    };
};
